import { RichText } from './RichText';
import { useMemo, useState } from 'preact/hooks';
import { TemporalInput } from './TemporalInput';
import { RatingInput } from './RatingInput';
import { FieldHeader } from './FieldHeader';
import { acknowledgmentValue, acknowledgmentLabel, fieldDescription, isEmptyAnswer } from '../../shared/answers';
import { questionTextLimit } from '../../shared/questionSettings';
import type { CustomAnswer, Field } from '../../shared/types';

interface Props { field: Field; value: CustomAnswer | undefined; onChange: (v: CustomAnswer) => void; error?: string | null }

const OTHER = '__other__';

function shuffled<T>(xs: T[], seed: string): T[] {
  // Deterministic per-session shuffle so re-renders do not reorder options.
  let h = 0; for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) | 0;
  const out = [...xs];
  for (let i = out.length - 1; i > 0; i--) { h = (h * 1103515245 + 12345) & 0x7fffffff; const j = h % (i + 1); [out[i], out[j]] = [out[j], out[i]]; }
  return out;
}

export function FieldRenderer({ field: f, value, onChange, error }: Props) {
  const help = fieldDescription(f);
  const seed = useMemo(() => crypto.randomUUID(), []);
  const options = useMemo(() => (f.shuffle ? shuffled(f.options ?? [], seed) : f.options ?? []), [f.options, f.shuffle, seed]);

  const rows = useMemo(() => f.shuffleRows ? shuffled(f.grid?.rows ?? [], seed) : f.grid?.rows ?? [], [f.grid?.rows, f.shuffleRows, seed]);
  if (f.type === 'section') {
    return <div class="q-section"><h2><RichText text={f.label} format={f.labelFormat} /></h2>{help && <p class="muted"><RichText text={help} format={f.helpFormat} /></p>}</div>;
  }
  if (f.type === 'title_text') {
    return <div class="q-title"><h3><RichText text={f.label} format={f.labelFormat} /></h3>{help && <p class="muted"><RichText text={help} format={f.helpFormat} /></p>}</div>;
  }

  const inputId = `q-${f.id}`;
  const labelled = ['short_text', 'paragraph', 'dropdown'].includes(f.type) || (f.type === 'date' && f.includeYear !== false) || (f.type === 'time' && f.timeMode !== 'duration');
  const head = (
    <>
      <FieldHeader htmlFor={labelled ? inputId : undefined} labelId={labelled ? undefined : `${inputId}-label`} labelClass="q-label" error={error} errorId={`${inputId}-error`} missing={f.required && isEmptyAnswer(f, value)}
        label={<><RichText text={f.label} format={f.labelFormat} />{f.required && <span class="required-label" aria-hidden="true">Required</span>}</>} />
      {help && <div class="muted small" id={`${inputId}-help`}><RichText text={help} format={f.helpFormat} /></div>}
    </>
  );
  // Text that has to fit somewhere, such as a box on a PDF, shows its limit and a running count before it gets too long.
  const limit = questionTextLimit(f);
  const aria = { 'aria-describedby': [error ? `${inputId}-error` : '', help ? `${inputId}-help` : '', limit ? `${inputId}-limit` : ''].filter(Boolean).join(' ') || undefined, 'aria-invalid': !!error, 'aria-required': f.required };
  const str = typeof value === 'string' ? value : '';
  const count = limit ? <p class="field-help text-limit" id={`${inputId}-limit`} aria-live="polite">{str.length.toLocaleString()} of {limit.toLocaleString()} characters</p> : null;
  // The error is in the label row (FieldHeader), so nothing goes beneath the control.
  const foot = null;

  switch (f.type) {
    case 'acknowledgment':
      return <div class="q-field acknowledgment-field">{head}
        <label class="acknowledgment-choice"><input id={inputId} type="checkbox" checked={value === acknowledgmentValue(f)} {...aria} aria-required={true}
          aria-describedby={[`${inputId}-label`, aria['aria-describedby']].filter(Boolean).join(' ')}
          onChange={(e) => onChange(e.currentTarget.checked ? acknowledgmentValue(f) : '')} /><span>{acknowledgmentLabel(f)}</span></label>{foot}
      </div>;
    case 'short_text':
      return <div class="q-field">{head}<input id={inputId} {...aria} placeholder="Short answer text" maxLength={limit} value={str} onInput={(e) => onChange((e.target as HTMLInputElement).value)} />{count}</div>;
    case 'paragraph':
      return <div class="q-field">{head}<textarea id={inputId} {...aria} placeholder="Long answer text" rows={4} maxLength={limit} value={str} onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)} />{count}{foot}</div>;
    case 'date':
    case 'time':
      return <div class="q-field">{head}<TemporalInput field={f} value={str} onChange={onChange} error={error} describedBy={aria['aria-describedby']} />{foot}</div>;
    case 'dropdown':
      return (
        <div class="q-field">{head}
          <select id={inputId} {...aria} value={str} onChange={(e) => onChange((e.target as HTMLSelectElement).value)}>
            <option value="">Choose…</option>
            {options.map((o) => <option value={o}>{o}</option>)}
          </select>{foot}
        </div>
      );
    case 'choice':
      return <ChoiceInput f={f} options={options} value={str} onChange={onChange} head={head} foot={foot} error={error} />;
    case 'checkboxes':
      return <CheckboxesInput f={f} options={options} value={Array.isArray(value) ? value : []} onChange={onChange} head={head} foot={foot} error={error} />;
    case 'rating':
      return <div class="q-field">{head}<RatingInput id={inputId} max={f.rating?.max ?? 5} icon={f.rating?.icon} value={str} onChange={onChange} error={!!error} describedBy={aria['aria-describedby']} required={f.required} />{foot}</div>;
    case 'linear_scale': {
      const s = f.scale ?? { min: 1, max: 5 };
      const nums: number[] = []; for (let n = s.min; n <= s.max; n++) nums.push(n);
      return (
        <div class="q-field">{head}
          <div class="scale" role="group" tabIndex={-1} aria-labelledby={`${inputId}-label`} {...aria}>
            {s.minLabel && <span class="muted small">{s.minLabel}</span>}
            {nums.map((n) => <label class="scale-opt"><span>{n}</span><input type="radio" name={f.id} checked={str === String(n)} onChange={() => onChange(String(n))} /></label>)}
            {s.maxLabel && <span class="muted small">{s.maxLabel}</span>}
          </div>{foot}
        </div>
      );
    }
    case 'choice_grid':
    case 'checkbox_grid': {
      const g = f.grid ?? { rows: [], columns: [] };
      const multi = f.type === 'checkbox_grid';
      const obj = (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<string, string | string[]>;
      const setRow = (r: string, v: string | string[]) => onChange({ ...obj, [r]: v });
      return (
        <div class="q-field">{head}
          <div class="grid-wrap" role="group" tabIndex={-1} aria-labelledby={`${inputId}-label`} {...aria}>
            <table class="grid">
              <thead><tr><th></th>{g.columns.map((c) => <th>{c}</th>)}</tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr><th>{r}</th>{g.columns.map((c) => (
                    <td>{multi
                      ? <input type="checkbox" aria-label={`${r}: ${c}`} checked={((obj[r] as string[]) ?? []).includes(c)} onChange={() => { const cur = (obj[r] as string[]) ?? []; setRow(r, cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]); }} />
                      : <input type="radio" aria-label={`${r}: ${c}`} name={`${f.id}:${r}`} checked={obj[r] === c} onChange={() => setRow(r, c)} />}
                    </td>
                  ))}</tr>
                ))}
              </tbody>
            </table>
          </div>{foot}
        </div>
      );
    }
  }
  return null;
}

function ChoiceInput({ f, options, value, onChange, head, foot, error }: { f: Field; options: string[]; value: string; onChange: (v: string) => void; head: preact.ComponentChildren; foot: preact.ComponentChildren; error?: string | null }) {
  const isOther = value !== '' && !options.includes(value);
  const [otherOn, setOtherOn] = useState(isOther);
  const [otherText, setOtherText] = useState(isOther ? value : '');
  return (
    <div class="q-field" role="group" tabIndex={-1} aria-invalid={!!error} aria-required={f.required} aria-describedby={error ? `q-${f.id}-error` : undefined} aria-labelledby={`q-${f.id}-label`}>{head}
      <div class="stack tight">
        {options.map((o) => <label class="inline"><input type="radio" name={f.id} checked={value === o} onChange={() => { setOtherOn(false); onChange(o); }} /> {o}</label>)}
        {f.allowOther && (
          <label class="inline"><input type="radio" name={f.id} value={OTHER} checked={otherOn} onChange={() => { setOtherOn(true); onChange(otherText); }} /> Other:
            <input value={otherText} onFocus={() => { setOtherOn(true); onChange(otherText); }} onInput={(e) => { const t = (e.target as HTMLInputElement).value; setOtherText(t); setOtherOn(true); onChange(t); }} />
          </label>
        )}
      </div>{foot}
    </div>
  );
}

function CheckboxesInput({ f, options, value, onChange, head, foot, error }: { f: Field; options: string[]; value: string[]; onChange: (v: string[]) => void; head: preact.ComponentChildren; foot: preact.ComponentChildren; error?: string | null }) {
  const otherVal = value.find((v) => !options.includes(v));
  const [otherOn, setOtherOn] = useState(otherVal !== undefined);
  const [otherText, setOtherText] = useState(otherVal ?? '');
  const base = value.filter((v) => options.includes(v));
  const emit = (b: string[], on: boolean, t: string) => onChange(on && t ? [...b, t] : b);
  return (
    <div class="q-field" role="group" tabIndex={-1} aria-invalid={!!error} aria-required={f.required} aria-describedby={error ? `q-${f.id}-error` : undefined} aria-labelledby={`q-${f.id}-label`}>{head}
      <div class="stack tight">
        {options.map((o) => <label class="inline"><input type="checkbox" checked={base.includes(o)} onChange={() => emit(base.includes(o) ? base.filter((x) => x !== o) : [...base, o], otherOn, otherText)} /> {o}</label>)}
        {f.allowOther && (
          <label class="inline"><input type="checkbox" checked={otherOn} onChange={() => { setOtherOn(!otherOn); emit(base, !otherOn, otherText); }} /> Other:
            <input value={otherText} onInput={(e) => { const t = (e.target as HTMLInputElement).value; setOtherText(t); setOtherOn(true); emit(base, true, t); }} />
          </label>
        )}
      </div>{foot}
    </div>
  );
}
